// =============================================================
// offlinePack.ts — fetch list of region venues + tell the service worker
// to pre-cache each venue's detail + .mobileconfig for offline use.
//
// Patrick rule 2026-04-26: when on WiFi, cache the entire VN venue set so
// the PWA works offline (walking around with no data on iPhone). Triggered
// once per session on first launch (or manually via Settings later).
// =============================================================

// Vite app: must use import.meta.env, NOT process.env. process is undefined
// in iOS Safari at runtime → ReferenceError → ErrorBoundary catches → user
// sees "Oops! Something went wrong" page on every venue load.
const API_BASE = ((import.meta as any).env?.VITE_API_URL || 'https://core.honestlee.app').replace(/\/$/, '');

export interface OfflinePackProgress {
  done: number;
  failed: number;
  total: number;
  phase: 'fetching-list' | 'caching' | 'complete' | 'error';
  message?: string;
}

const STORAGE_KEY = 'hl_offline_pack_last';

/** True if the offline pack has been built within the last 24h (no need to redo). */
export function offlinePackIsFresh(maxAgeHours = 24): boolean {
  const last = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
  return last > 0 && (Date.now() - last) < maxAgeHours * 60 * 60 * 1000;
}

/**
 * Build the offline pack:
 *   1. Fetch /api/venues/map?region=<region> → list of slugs
 *   2. Build URL list: each /api/venues/:slug + each /api/venues/:slug/wifi.mobileconfig
 *   3. Send to service worker via postMessage; SW caches each in parallel
 *   4. Stream progress callbacks to the caller (so UI can show "247/567")
 */
export async function buildOfflinePack(
  region: string = 'vn',
  onProgress?: (p: OfflinePackProgress) => void,
): Promise<{ ok: boolean; cachedCount: number; failedCount: number }> {
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
    onProgress?.({ done: 0, failed: 0, total: 0, phase: 'error', message: 'Service worker not active. Refresh and try again.' });
    return { ok: false, cachedCount: 0, failedCount: 0 };
  }

  onProgress?.({ done: 0, failed: 0, total: 0, phase: 'fetching-list', message: `Fetching ${region.toUpperCase()} venue list…` });

  let venues: any[];
  try {
    const r = await fetch(`${API_BASE}/api/venues/map?region=${encodeURIComponent(region)}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    venues = d.venues || d.data || (Array.isArray(d) ? d : []);
  } catch (e: any) {
    onProgress?.({ done: 0, failed: 0, total: 0, phase: 'error', message: `Failed to fetch venue list: ${e.message}` });
    return { ok: false, cachedCount: 0, failedCount: 0 };
  }

  // Slim map endpoint uses single-letter keys: 's' = slug
  const slugs: string[] = venues.map(v => v.s || v.slug).filter(Boolean);
  if (slugs.length === 0) {
    onProgress?.({ done: 0, failed: 0, total: 0, phase: 'error', message: 'No venues returned from API.' });
    return { ok: false, cachedCount: 0, failedCount: 0 };
  }

  // Build full URL list to pre-cache
  const urls: string[] = [
    `${API_BASE}/api/venues/map?region=${encodeURIComponent(region)}`,
  ];
  for (const slug of slugs) {
    urls.push(`${API_BASE}/api/venues/${encodeURIComponent(slug)}`);
    urls.push(`${API_BASE}/api/venues/${encodeURIComponent(slug)}/wifi.mobileconfig`);
  }

  onProgress?.({ done: 0, failed: 0, total: urls.length, phase: 'caching', message: `Caching ${slugs.length} venues + WiFi profiles…` });

  return new Promise((resolve) => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'PRECACHE_PROGRESS') {
        onProgress?.({
          done: event.data.done,
          failed: event.data.failed,
          total: event.data.total,
          phase: 'caching',
        });
      } else if (event.data?.type === 'PRECACHE_COMPLETE') {
        navigator.serviceWorker.removeEventListener('message', handler);
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
        onProgress?.({
          done: event.data.done,
          failed: event.data.failed,
          total: event.data.total,
          phase: 'complete',
          message: `Cached ${event.data.done} of ${event.data.total} (${event.data.failed} failed). Offline ready.`,
        });
        resolve({ ok: true, cachedCount: event.data.done, failedCount: event.data.failed });
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    navigator.serviceWorker.controller!.postMessage({ type: 'PRECACHE_OFFLINE_PACK', urls });
  });
}

/** Auto-trigger on app start if not fresh + on WiFi (skip on cellular to save data). */
export function autoBuildOfflinePackIfNeeded(region: string = 'vn'): void {
  if (offlinePackIsFresh()) return;
  // Only auto-build when on WiFi (don't burn cellular data without consent)
  const conn: any = (navigator as any).connection;
  const isWifi = !conn || conn.type === 'wifi' || conn.effectiveType === '4g';
  if (!conn?.saveData && isWifi) {
    // Wait 5 sec after page load so initial render isn't blocked
    setTimeout(() => {
      buildOfflinePack(region, (p) => {
        if (p.phase === 'complete') {
          console.log('[OfflinePack] Auto-cache complete:', p.message);
        }
      }).catch(e => console.warn('[OfflinePack] Auto-cache failed:', e));
    }, 5000);
  }
}
